/**
 * Employee Desktop Layout — Version web desktop de l'espace salarié
 * Auth via driver_access_token (même session que l'app chauffeur)
 * Accès réservé aux fleet_status === 'interne'
 */
import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  FileText, FolderOpen, Receipt, Calendar, Palmtree, Clock,
  User, LogOut, ChevronRight, Briefcase, LayoutDashboard, Bell
} from 'lucide-react';
import config from 'config';

const API = config.API_URL;

// Context to share employee data across desktop pages
const EmployeeDesktopContext = createContext(null);
export const useEmployeeDesktop = () => useContext(EmployeeDesktopContext);

const SIDEBAR_NAV = [
  { to: '/employees', label: 'Tableau de bord', icon: LayoutDashboard, end: true },
  { to: '/employees/contract', label: 'Mon contrat', icon: FileText },
  { to: '/employees/documents', label: 'Documents RH', icon: FolderOpen },
  { to: '/employees/payslips', label: 'Bulletins de salaire', icon: Receipt },
  { to: '/employees/schedule', label: 'Mon planning', icon: Calendar },
  { to: '/employees/leave', label: 'Mes congés', icon: Palmtree },
  { to: '/employees/history', label: 'Historique heures', icon: Clock },
];

export default function EmployeeDesktopLayout() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);

  const token = localStorage.getItem('driver_access_token');

  const fetchUser = useCallback(async (t) => {
    try {
      const res = await fetch(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (!res.ok) return null;
      return await res.json();
    } catch { return null; }
  }, []);

  const fetchDashboard = useCallback(async (t) => {
    try {
      const res = await fetch(`${API}/api/employees/me/dashboard`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (res.ok) return await res.json();
    } catch {}
    return null;
  }, []);

  const fetchNotifications = useCallback(async (t) => {
    try {
      const res = await fetch(`${API}/api/employees/me/notifications`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    (async () => {
      const u = await fetchUser(token);
      if (u && u.fleet_status === 'interne') {
        setUser(u);
        const d = await fetchDashboard(token);
        setDashboard(d);
        fetchNotifications(token);
      } else if (u) {
        setUser({ forbidden: true });
      }
      setLoading(false);
    })();
  }, [token, fetchUser, fetchDashboard]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword }),
      });
      const data = await res.json();
      if (res.ok && data.access_token) {
        localStorage.setItem('driver_access_token', data.access_token);
        if (data.refresh_token) localStorage.setItem('driver_refresh_token', data.refresh_token);
        const u = data.user || await fetchUser(data.access_token);
        if (u?.fleet_status === 'interne') {
          setUser(u);
          const d = await fetchDashboard(data.access_token);
          setDashboard(d);
          fetchNotifications(data.access_token);
        } else {
          setUser({ forbidden: true });
        }
      } else {
        setLoginError(data.detail || 'Identifiants incorrects');
      }
    } catch {
      setLoginError('Erreur de connexion');
    }
    setLoginLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('driver_access_token');
    localStorage.removeItem('driver_refresh_token');
    setUser(null);
    setDashboard(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050509] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#C9A24D]/20 border-t-[#C9A24D] rounded-full animate-spin" />
      </div>
    );
  }

  // Login screen
  if (!user) {
    return (
      <div className="min-h-screen bg-[#050509] flex items-center justify-center p-4" data-testid="employee-login-page">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-[#C9A24D]/15 flex items-center justify-center mx-auto mb-4">
              <Briefcase className="w-8 h-8 text-[#C9A24D]" />
            </div>
            <h1 className="text-2xl font-semibold text-white">Espace Salarié</h1>
            <p className="text-white/40 text-sm mt-2">Connectez-vous avec vos identifiants chauffeur</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4 p-6 rounded-2xl bg-white/[0.03] border border-white/5">
            {loginError && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="login-error">
                {loginError}
              </div>
            )}
            <div>
              <label className="text-white/60 text-xs font-medium mb-1.5 block">Email</label>
              <input
                type="email"
                value={loginEmail}
                onChange={e => setLoginEmail(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder:text-white/20 focus:border-[#C9A24D]/30 focus:outline-none transition"
                placeholder="votre.email@y-go.fr"
                required
                data-testid="employee-login-email"
              />
            </div>
            <div>
              <label className="text-white/60 text-xs font-medium mb-1.5 block">Mot de passe</label>
              <input
                type="password"
                value={loginPassword}
                onChange={e => setLoginPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder:text-white/20 focus:border-[#C9A24D]/30 focus:outline-none transition"
                placeholder="Mot de passe"
                required
                data-testid="employee-login-password"
              />
            </div>
            <button
              type="submit"
              disabled={loginLoading}
              className="w-full py-3 rounded-xl bg-[#C9A24D] text-black font-semibold text-sm hover:bg-[#d4af5a] disabled:opacity-50 transition"
              data-testid="employee-login-submit"
            >
              {loginLoading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>

          <p className="text-center text-white/20 text-xs mt-6">
            Accès réservé aux salariés internes Y GO
          </p>
        </div>
      </div>
    );
  }

  // Forbidden screen
  if (user.forbidden) {
    return (
      <div className="min-h-screen bg-[#050509] flex items-center justify-center p-4" data-testid="employee-forbidden-page">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <Briefcase className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-semibold text-white mb-2">Accès refusé</h1>
          <p className="text-white/40 text-sm">Cet espace est réservé aux salariés internes Y GO.</p>
          <button
            onClick={handleLogout}
            className="mt-6 px-6 py-2 rounded-xl bg-white/5 text-white/60 text-sm hover:text-white transition"
            data-testid="forbidden-logout-btn"
          >
            Se déconnecter
          </button>
        </div>
      </div>
    );
  }

  // Main desktop layout
  return (
    <EmployeeDesktopContext.Provider value={{ user, dashboard, token: localStorage.getItem('driver_access_token') }}>
      <div className="min-h-screen bg-[#050509] flex" data-testid="employee-desktop-layout">
        {/* Fixed sidebar */}
        <aside className="w-72 bg-[#0B0B0B] border-r border-white/5 flex flex-col fixed inset-y-0 left-0 z-40" data-testid="desktop-sidebar">
          {/* Logo */}
          <div className="p-6 border-b border-white/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#C9A24D]/15 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-[#C9A24D]" />
              </div>
              <div>
                <h2 className="text-white font-semibold text-sm">Espace Salarié</h2>
                <p className="text-[#C9A24D] text-xs">Y GO VTC</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-testid="desktop-nav">
            {SIDEBAR_NAV.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all group ${
                    isActive
                      ? 'bg-[#C9A24D]/10 text-[#C9A24D] border border-[#C9A24D]/15'
                      : 'text-white/50 hover:text-white hover:bg-white/[0.03]'
                  }`
                }
                data-testid={`desktop-nav-${item.to.split('/').pop() || 'dashboard'}`}
              >
                <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
                <span className="flex-1">{item.label}</span>
                <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-50 transition" />
              </NavLink>
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-white/5">
            {/* Notification bell */}
            <div className="relative mb-3 px-3">
              <button
                onClick={() => setShowNotifs(!showNotifs)}
                className="flex items-center gap-3 w-full px-3 py-2 rounded-xl text-white/50 hover:text-white hover:bg-white/[0.03] transition text-sm"
                data-testid="desktop-notif-bell"
              >
                <Bell className="w-[18px] h-[18px]" />
                <span>Notifications</span>
                {unreadCount > 0 && (
                  <span className="ml-auto w-5 h-5 rounded-full bg-[#C9A24D] text-black text-[10px] font-bold flex items-center justify-center" data-testid="notif-badge">{unreadCount}</span>
                )}
              </button>

              {showNotifs && (
                <div className="absolute bottom-full left-3 right-3 mb-2 max-h-[320px] overflow-y-auto rounded-xl bg-[#111] border border-white/10 shadow-2xl z-50" data-testid="notif-panel">
                  <div className="p-3 border-b border-white/5 flex items-center justify-between">
                    <span className="text-white text-xs font-semibold">Notifications</span>
                    {unreadCount > 0 && (
                      <button
                        onClick={async () => {
                          const t = localStorage.getItem('driver_access_token');
                          await fetch(`${API}/api/employees/me/notifications/read-all`, {
                            method: 'PATCH', headers: { Authorization: `Bearer ${t}` },
                          });
                          fetchNotifications(t);
                        }}
                        className="text-[#C9A24D] text-[10px] font-medium hover:underline"
                        data-testid="notif-read-all"
                      >
                        Tout marquer comme lu
                      </button>
                    )}
                  </div>
                  {notifications.length === 0 ? (
                    <p className="p-4 text-white/20 text-xs text-center">Aucune notification</p>
                  ) : (
                    notifications.slice(0, 15).map(n => (
                      <div
                        key={n.id}
                        className={`px-3 py-2.5 border-b border-white/5 last:border-0 ${n.is_read ? 'opacity-50' : ''}`}
                        data-testid={`notif-item-${n.id}`}
                      >
                        <p className="text-white text-xs">{n.message}</p>
                        <p className="text-white/20 text-[10px] mt-0.5">{new Date(n.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 px-3 py-2">
              <div className="w-9 h-9 rounded-full bg-[#C9A24D]/15 flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-[#C9A24D]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{dashboard?.driver_name || user?.first_name}</p>
                <p className="text-white/30 text-xs">Salarié interne</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 rounded-lg text-white/30 hover:text-red-400 transition"
                title="Se déconnecter"
                data-testid="desktop-logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 ml-72 min-h-screen" data-testid="desktop-main-content">
          <Outlet context={{ user, dashboard, token: localStorage.getItem('driver_access_token') }} />
        </main>
      </div>
    </EmployeeDesktopContext.Provider>
  );
}
